import { useEffect, useState, useCallback } from "react";
import { DataTable, ColumnDef, FilterOption } from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Download } from "lucide-react";
// import { api, ApiClientError } from "@/lib/api"; // Uncomment when backend is ready

// Define the SearchEngine type
interface SearchEngine {
  id: string;
  search_engine_name: string;
  ncii_instances: number;
  type: string;
  region: string;
  active_urls: number;
  removed_urls: number;
  takedown_rate: number; // Percentage as decimal (e.g., 0.85 for 85%)
  avg_response_time: number; // In days
  contacts: string[]; // Array of email addresses
}

const PAGE_SIZE = 50;

export function SearchEngineList() {
  const { logout } = useAuth();
  const [searchEngines, setSearchEngines] = useState<SearchEngine[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [_totalCount, setTotalCount] = useState<number>(0);

  const loadSearchEngines = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // TODO: Replace with actual API call
      // const data = await api.searchEngines.list(PAGE_SIZE, currentPage);
      // setSearchEngines(data);
      // setHasMore(data.length === PAGE_SIZE);
      // setTotalCount(data.totalCount); // Assuming API returns total count

      // Mock data for now
      const mockSearchEngines: SearchEngine[] = [
        {
          id: "1",
          search_engine_name: "Google",
          ncii_instances: 2134,
          type: "General",
          region: "Global",
          active_urls: 398,
          removed_urls: 1736,
          takedown_rate: 0.81,
          avg_response_time: 1.8,
          contacts: ["abuse@google.com", "removals@google.com"],
        },
        {
          id: "2",
          search_engine_name: "Bing",
          ncii_instances: 1234,
          type: "General",
          region: "Global",
          active_urls: 230,
          removed_urls: 1004,
          takedown_rate: 0.81,
          avg_response_time: 2.1,
          contacts: ["abuse@microsoft.com", "bing-removals@microsoft.com"],
        },
        {
          id: "3",
          search_engine_name: "Yahoo",
          ncii_instances: 892,
          type: "General",
          region: "Global",
          active_urls: 166,
          removed_urls: 726,
          takedown_rate: 0.81,
          avg_response_time: 2.5,
          contacts: ["abuse@yahoo.com"],
        },
        {
          id: "4",
          search_engine_name: "DuckDuckGo",
          ncii_instances: 456,
          type: "Privacy-focused",
          region: "Global",
          active_urls: 85,
          removed_urls: 371,
          takedown_rate: 0.81,
          avg_response_time: 1.9,
          contacts: ["abuse@duckduckgo.com", "legal@duckduckgo.com"],
        },
        {
          id: "5",
          search_engine_name: "Baidu",
          ncii_instances: 1567,
          type: "General",
          region: "Asia",
          active_urls: 292,
          removed_urls: 1275,
          takedown_rate: 0.81,
          avg_response_time: 3.2,
          contacts: ["abuse@baidu.com"],
        },
        {
          id: "6",
          search_engine_name: "Yandex",
          ncii_instances: 734,
          type: "General",
          region: "Europe",
          active_urls: 137,
          removed_urls: 597,
          takedown_rate: 0.81,
          avg_response_time: 2.8,
          contacts: ["abuse@yandex.com", "legal@yandex.ru"],
        },
        {
          id: "7",
          search_engine_name: "Naver",
          ncii_instances: 398,
          type: "General",
          region: "Asia",
          active_urls: 74,
          removed_urls: 324,
          takedown_rate: 0.81,
          avg_response_time: 2.6,
          contacts: ["abuse@naver.com"],
        },
        {
          id: "8",
          search_engine_name: "Ecosia",
          ncii_instances: 189,
          type: "Eco-friendly",
          region: "Global",
          active_urls: 35,
          removed_urls: 154,
          takedown_rate: 0.81,
          avg_response_time: 2.0,
          contacts: ["abuse@ecosia.org"],
        },
        {
          id: "9",
          search_engine_name: "Brave Search",
          ncii_instances: 234,
          type: "Privacy-focused",
          region: "Global",
          active_urls: 44,
          removed_urls: 190,
          takedown_rate: 0.81,
          avg_response_time: 1.7,
          contacts: ["abuse@brave.com", "search-abuse@brave.com"],
        },
        {
          id: "10",
          search_engine_name: "Sogou",
          ncii_instances: 523,
          type: "General",
          region: "Asia",
          active_urls: 97,
          removed_urls: 426,
          takedown_rate: 0.81,
          avg_response_time: 3.0,
          contacts: ["abuse@sogou.com"],
        },
      ];
      setSearchEngines(mockSearchEngines);
      setHasMore(false);
      setTotalCount(mockSearchEngines.length); // For mock data, total equals current data length
    } catch (err) {
      // if (err instanceof ApiClientError) {
      //   if (err.status === 401) {
      //     logout();
      //   } else {
      //     setError(err.detail || "Failed to load search engines");
      //   }
      // } else {
      //   setError("Failed to load search engines");
      // }
      setError("Failed to load search engines");
    } finally {
      setLoading(false);
    }
  }, [currentPage, logout]);

  useEffect(() => {
    loadSearchEngines();
  }, [loadSearchEngines]);

  const handleNextPage = () => {
    if (!loading && hasMore) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (!loading && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  // Define columns for the Search Engines table
  const columns: ColumnDef<SearchEngine>[] = [
    {
      key: "search_engine_name",
      header: "Search Engine Name",
      render: (item) => (
        <div className="text-xs font-medium text-foreground">
          {item.search_engine_name}
        </div>
      ),
      sortValue: (item) => item.search_engine_name,
    },
    {
      key: "ncii_instances",
      header: "NCII Instances",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.ncii_instances}
        </div>
      ),
      sortValue: (item) => item.ncii_instances,
    },
    {
      key: "type",
      header: "Type",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.type}</div>
      ),
      sortValue: (item) => item.type,
    },
    {
      key: "region",
      header: "Region",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.region}</div>
      ),
      sortValue: (item) => item.region,
    },
    {
      key: "active_urls",
      header: "Active URLs",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.active_urls}</div>
      ),
      sortValue: (item) => item.active_urls,
    },
    {
      key: "removed_urls",
      header: "Removed URLs",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.removed_urls}</div>
      ),
      sortValue: (item) => item.removed_urls,
    },
    {
      key: "takedown_rate",
      header: "Takedown Rate",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {(item.takedown_rate * 100).toFixed(1)}%
        </div>
      ),
      sortValue: (item) => item.takedown_rate,
    },
    {
      key: "avg_response_time",
      header: "Avg Response Time",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.avg_response_time.toFixed(1)} days
        </div>
      ),
      sortValue: (item) => item.avg_response_time,
    },
    {
      key: "contacts",
      header: "Contacts",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.contacts.join(", ")}
        </div>
      ),
      sortValue: (item) => item.contacts.join(", "),
    },
    {
      key: "actions",
      header: "Actions",
      sortable: false,
      render: (item) => (
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-3"
          onClick={() => {
            // TODO: Navigate to search engine details or open modal
            console.log("View search engine:", item.id);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  // Define filters (for search functionality)
  const filters: FilterOption[] = [
    { label: "Type", placeholder: "All Types" },
    { label: "Region", placeholder: "All Regions" },
  ];

  // Handle new search engine creation
  const handleNewSearchEngine = () => {
    // TODO: Implement new search engine modal or navigation
    console.log("Add new search engine");
  };

  // Handle export
  const handleExport = () => {
    // TODO: Implement export functionality
    console.log("Export search engines");
  };

  // Action buttons for the header
  const actionButtons = (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleExport}
        className="h-9 px-4"
      >
        <Download className="h-4 w-4 mr-2" />
        Export
      </Button>
      <Button
        size="sm"
        onClick={handleNewSearchEngine}
        className="h-9 px-4"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Search Engine
      </Button>
    </>
  );

  return (
    <DataTable
      title="Search Engines"
      subtitle="Track search engine de-indexing and removal"
      columns={columns}
      data={searchEngines}
      loading={loading}
      error={error}
      filters={filters}
      searchPlaceholder="Search search engines..."
      onNextPage={handleNextPage}
      onPreviousPage={handlePreviousPage}
      hasNextPage={hasMore}
      hasPreviousPage={currentPage > 1}
      currentPage={currentPage}
      itemsPerPage={PAGE_SIZE}
      actionButtons={actionButtons}
      totalCount={undefined}
      entityName="search engines"
    />
  );
}



